import axios from 'axios';
import dayjs, { CHURCH_TIMEZONE } from '@/utils/date';
import EGIRO_STATES from '@/constants/MyGiving/EGIRO_STATES';
import transformTitheData from '@/utils/transform-tithe';
import { isEgiroHarnessActive, harnessAccounts } from '@/utils/egiro-harness';
import { fetchPledgeYears } from '@/utils/building-fund-pledge-api';
import { fakeFastStatus, isBfFastHarnessTransaction } from '@/utils/bf-pledge-harness';
import { PIN_SESSION_HEADER, NO_PIN_SESSION, givingPinApiUrl } from '@/utils/giving-pin-session';
import {
  GIVING_LOGIN_STATES,
  GIVING_LOGIN_STATE_PATH,
  VERIFY_PERSONAL_ID_PATH,
  isGivingLoginUnverified,
  mapVerifyPersonalIdError,
  normalizeLoginState,
  verifyPersonalIdPayload,
} from '@/utils/giving-login';

/**
 * The API returns `giving_date` as a UTC instant. Members read it as the
 * Singapore date printed on their bank statement, so rewrite it in place to a
 * Singapore wall clock rather than leaving it to the device's timezone.
 */
const toSingaporeGivingDates = records => {
  for (const record of records ?? []) {
    record.giving_date = dayjs
      .utc(record.giving_date)
      .tz(CHURCH_TIMEZONE)
      .format('YYYY-MM-DDTHH:mm:ss');
  }
  return records;
};

// An amount as MyGiving's amount fields write it once blurred: `1500` → `1,500.00`.
const formatAmountField = amount =>
  Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const INITIAL_STATE = {
  isLoading: false,
  showRecurringGiving: false,
  givingResources: [],
  linkedAccounts: [],
  recurringGivingList: [],
  selectedGiveType: 'Offering',
  selectedGiveMethod: 'paynow-qr',
  selectedLinkedAccount: null,
  availableLimitForSelectedAccount: 0,
  isFromGivenNow: false,
  // An amount Give now opens with, as `{ fund, amount }`: a BF2026 one-time pledge
  // the member has just made. Give now (giving-options) clears it when it is opened
  // for anything else, so it never outlives that one hand-off.
  giveNowPrefill: null,
  eGiroMandateId: null,
  tithingState: 3,
  titheYears: null,
  titheStart: false,
  titheContinue: false,
  bfPledgeYears: null,
  selectedYear: null,
  //Pin
  pinStatus: null,
  pinAttemptsLeft: null,
  pinLockedUntil: null,
  lastVerifiedAt: null,
  pinVerifiedThisSession: false,
  // Server-side PIN session id (see @/utils/giving-pin-session). Null until a
  // hogc-one-api that issues one answers a correct PIN.
  pinSessionId: null,
  resetEmail: '',
  resetMaskedEmail: '',
  // Giving per login (@/utils/giving-login). Null: not known, or an hogc-one-api
  // that predates it; MyGiving then behaves as it always has.
  loginGivingState: null,
  isPrimaryLogin: null,
  //Building Fund PLEDGE
  bfPledge: null,

  //secondary couple pledge
  secondaryPledge: false,
  couplePledgeData: null,
  mainPledgerName: null, // Store main pledger's name when viewing as secondary pledger

  //General Fund PLEDGE
  gfPledge: null,

  //Heart Community Services Fund PLEDGE
  hcsPledge: null,

  //Building Fund FULFILLMENT record
  bfFulfillmentRecord: null,

  //GenerationS Fund FULFILLMENT record
  gfFulfillmentRecord: null,

  //Heart Community Services Fund FULFILLMENT record
  hcsFulfillmentRecord: null,

  //Tithe record
  titheRecord: [
    {
      giving_type: 'Tithe',
      amount: true,
      ns_fulfillment_date: '2025-06-05T16:20:00.000Z',
      createdAt: '2025-06-05T16:20:00.000Z',
      updatedAt: '2025-06-05T16:20:00.000Z',
    },
    {
      giving_type: 'Tithe',
      amount: true,
      ns_fulfillment_date: '2025-07-05T16:20:00.000Z',
      createdAt: '2025-07-05T16:20:00.000Z',
      updatedAt: '2025-07-05T16:20:00.000Z',
    },
    {
      giving_type: 'Tithe',
      amount: true,
      ns_fulfillment_date: '2025-08-05T16:20:00.000Z',
      createdAt: '2025-08-05T16:20:00.000Z',
      updatedAt: '2025-08-05T16:20:00.000Z',
    },
  ],

  //transformed tithe records, array of 12 for each month, if month has no record : titheTableRecords[i]="-"
  titheTableRecords: [],
};

// A fresh copy per store: returning INITIAL_STATE itself let every registration
// share (and mutate) one object.
const state = () => JSON.parse(JSON.stringify(INITIAL_STATE));

/**
 * A record call failed. giving-api's GIVING_LOGIN_UNVERIFIED is not an error to the
 * member: the landing page shows the personal-id check instead, so no toast.
 */
const reportRecordError = ({ commit, dispatch }, error, message) => {
  if (isGivingLoginUnverified(error)) {
    commit('markLoginUnverified');
    return;
  }
  dispatch('toast/error', message, { root: true });
};

const getters = {
  // What an amount screen (PayNow, FAST) opens with: the hand-off's amount when the
  // member kept its fund, else null for a blank field.
  giveNowAmount: state =>
    state.giveNowPrefill?.fund === state.selectedGiveType && state.giveNowPrefill.amount > 0
      ? formatAmountField(state.giveNowPrefill.amount)
      : null,
  linkedBankAccounts: state => {
    // 'Linked' is the member_linked_account name; 'Success' is the pre-migration
    // name for the same thing. Accept both — matching only 'Linked' made any
    // un-migrated row silently invisible AND unusable as a payment method, with
    // no error anywhere to explain it (ONE-8186).
    return state.linkedAccounts.filter(
      acc => acc.state === EGIRO_STATES.LINKED || acc.state === EGIRO_STATES.SUCCESS,
    );
  },
  bankAccountsPendingApproval: state => {
    return state.linkedAccounts.filter(
      acc => acc.state === EGIRO_STATES.PENDING_APPROVAL || acc.state === EGIRO_STATES.SUBMITTED,
    );
  },
  isPinVerifiedThisSession: state => state.pinVerifiedThisSession,
  // This login must confirm the personal id before it sees any giving records.
  needsGivingVerification: state =>
    state.loginGivingState === GIVING_LOGIN_STATES.VERIFICATION_REQUIRED,
  // The member has no personal id on record, so no login has giving records yet.
  hasNoPersonalId: state => state.loginGivingState === GIVING_LOGIN_STATES.NO_PERSONAL_ID,
  // False only for a confirmed non-primary login of a member with several.
  isSecondaryLogin: state => state.isPrimaryLogin === false,
  bankAccountsPendingRemoval: state => {
    // Use 'Pending_Removal' state from member_linked_account table
    return state.linkedAccounts.filter(acc => acc.state === EGIRO_STATES.PENDING_REMOVAL);
  },
  // Mandates the bank rejected. Without this the row is returned by the API and
  // rendered by nothing, so a member whose authorisation failed comes back to
  // the untouched empty state as if they never tried (ONE-8181).
  bankAccountsFailed: state => {
    return state.linkedAccounts.filter(acc => acc.state === EGIRO_STATES.FAILED);
  },
};

const mutations = {
  // set states
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setShowRecurringGiving(state, showRecurringGiving) {
    state.showRecurringGiving = showRecurringGiving;
  },
  setGivingResources(state, givingResources) {
    state.givingResources = givingResources;
  },
  //for tithe
  setTithingState(state, tithingState) {
    state.tithingState = tithingState;
  },
  setTitheYears(state, titheYears) {
    state.titheYears = titheYears;
  },
  setTitheRecordByYear(state, titheRecord) {
    state.titheRecord = titheRecord;
  },
  // for building fund
  setBfPledgeYears(state, bfYears) {
    state.bfPledgeYears = bfYears;
  },
  setGfRecordByYear(state, gfRecord) {
    state.gfFulfillmentRecord = toSingaporeGivingDates(gfRecord);
  },
  setHcsRecordByYear(state, hcsRecord) {
    state.hcsFulfillmentRecord = toSingaporeGivingDates(hcsRecord);
  },
  setBfRecordByYear(state, bfRecord) {
    state.bfFulfillmentRecord = toSingaporeGivingDates(bfRecord);
  },
  setBfPledgeByYear(state, bfPledge) {
    state.bfPledge = bfPledge;
  },
  setGfPledgeByYear(state, gfPledge) {
    state.gfPledge = gfPledge;
  },
  setHcsPledgeByYear(state, hcsPledge) {
    state.hcsPledge = hcsPledge;
  },
  setTitheStartByYear(state, titheStart) {
    state.titheStart = titheStart;
  },
  setTitheContinueByYear(state, titheContinue) {
    state.titheContinue = titheContinue;
  },
  setSelectedYear(state, year) {
    state.selectedYear = year;
  },
  setTitheTableRecords(state) {
    // for (let i = 0; i < 12; i++) {
    //   state.titheTableRecords[i] = "-";
    // }
    // console.log(state.titheRecord.data)
    // console.log(transformTitheData(state.titheRecord.data))
    // for (let i = 0; i < state.titheRecord.data.length; i++) {
    //   var fullDate = state.titheRecord.data[i].ns_fulfillment_date;
    //   fullDate = moment.utc(fullDate).tz("Asia/Singapore").format("YYYY-MM-DD");
    //   const indexStr = fullDate.substring(5, 7);
    //   const index = parseInt(indexStr, 10) - 1;
    //   if (state.titheTableRecords[index] == "-") {
    //     //each titheRecord index is respective month
    //     //titheRecord[0] is January, titheRecord[1] is February
    //     //set first known record of that month
    //     //remains "-" if no records for that month
    //     const date = fullDate.substring(0, 10);
    //     const formattedDate = dayjs(date).format("DD MMM YYYY");
    //     state.titheTableRecords[index] = formattedDate;
    //   }
    // }
    state.titheTableRecords = transformTitheData(state.titheRecord.data);
    console.log(state.titheTableRecords);
  },
  setGivingType(state, selectedGiveType) {
    state.selectedGiveType = selectedGiveType;
  },
  setGivingMethod(state, selectedGiveMethod) {
    state.selectedGiveMethod = selectedGiveMethod;
  },
  setGivingAccount(state, selectedLinkedAccount) {
    state.selectedLinkedAccount = selectedLinkedAccount;
  },
  setAvailableLimitForSelectedAccount(state, availableLimitForSelectedAccount) {
    state.availableLimitForSelectedAccount = availableLimitForSelectedAccount;
  },
  setIsFromGivenNow(state, isFromGivenNow) {
    state.isFromGivenNow = isFromGivenNow;
  },
  setGiveNowPrefill(state, prefill) {
    state.giveNowPrefill = prefill;
  },
  setEGiroMandateId(state, eGiroMandateId) {
    state.eGiroMandateId = eGiroMandateId;
  },
  setSecondaryPledge(state, secondaryPledge) {
    state.secondaryPledge = secondaryPledge;
  },
  setCouplePledgeData(state, couplePledgeData) {
    state.couplePledgeData = couplePledgeData;
  },
  setMainPledgerName(state, mainPledgerName) {
    state.mainPledgerName = mainPledgerName;
  },
  setLinkedAccount(state, linkedAccounts) {
    state.linkedAccounts = linkedAccounts;
  },
  setRecurringGivingList(state, recurringGivingList) {
    state.recurringGivingList = recurringGivingList;
  },
  setPinState(state, { status, attemptsLeft = null, lockedUntil = null }) {
    state.pinStatus = status;
    state.pinAttemptsLeft = attemptsLeft;
    state.pinLockedUntil = lockedUntil ? new Date(lockedUntil).toISOString() : null;
  },
  setPinVerifiedThisSession(state, verified) {
    state.pinVerifiedThisSession = verified;
  },
  setPinSessionId(state, pinSessionId) {
    state.pinSessionId = pinSessionId || null;
  },
  clearPinSession(state) {
    state.pinVerifiedThisSession = false;
    state.pinSessionId = null;
  },
  setLoginGivingState(state, loginState) {
    state.loginGivingState = loginState?.state ?? null;
    state.isPrimaryLogin = loginState ? loginState.isPrimaryLogin : null;
  },
  // giving-api refused a record route: this login has not verified. Whether it is
  // the primary is not in that answer, so it is left as it was.
  markLoginUnverified(state) {
    state.loginGivingState = GIVING_LOGIN_STATES.VERIFICATION_REQUIRED;
  },
  setResetEmail(state, { status, email, masked }) {
    state.resetEmail = email;
    state.resetMaskedEmail = masked;
    state.pinStatus = status;
  },
};

const actions = {
  // actions are asynch(program doesnt wait for actions to be done to continue each command)
  // fetch myGiving backend
  // getch myGiving CMS
  async getGivingResources({ commit, dispatch, rootState }) {
    try {
      commit('setIsLoading', true);
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(process.env.VUE_APP_API_URL + '/giving/resources', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (status !== 200) throw new Error('Failed in getting giving resources');
      commit('setGivingResources', data);

      //commit("setIsLoading", false);
    } catch (e) {
      dispatch('toast/error', 'Failed to get giving resources.', {
        root: true,
      });
    }
  },
  async getTitheYears({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/tithes/get-tithe-year',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting tithe years');
      const yearArray = data.data;
      const sortedYears = yearArray.sort((a, b) => b - a);
      commit('setTitheYears', sortedYears);
      return data;
    } catch (e) {
      reportRecordError({ commit, dispatch }, e, 'Failed to retrieve tithe years');
    }
  },
  // Through the BF2026 pledge's API helper, so its dev harness answers for the
  // year list too. Returns `{ data: years }`, newest first, as the pages read it.
  async getBFPledgeYears({ commit, dispatch, rootState }) {
    try {
      const years = await fetchPledgeYears(rootState.user.token);
      const sortedYears = years.sort((a, b) => b - a);
      commit('setBfPledgeYears', sortedYears);
      return { data: sortedYears };
    } catch (e) {
      reportRecordError({ commit, dispatch }, e, 'Failed to retrieve building years');
    }
  },
  async getBFPledgeByYears({ commit, dispatch, rootState }, year) {
    try {
      const accessToken = rootState.user.token;
      // giving-api merges the member's separate 'Tithed' pledge into this
      // response (tithe / tithe_continue, or the whole row when there is no BF
      // pledge), as building-fund-api did (giving-api#175). The tithing rows on
      // the Building Fund page read those flags.
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/pledges/confirm?year=' + year,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting building fund years');
      // Use the secondary flag from API response to determine if user is viewing as secondary pledger
      const isSecondary = data.data?.secondary === true;
      commit('setSecondaryPledge', isSecondary);
      commit('setCouplePledgeData', data.data?.couplePledge ?? null);
      // Store main pledger's name when viewing as secondary pledger (full_name is the main pledger's name in this case)
      commit('setMainPledgerName', isSecondary ? (data.data?.full_name ?? null) : null);

      commit('setBfPledgeByYear', data.data.bf);
      commit('setGfPledgeByYear', data.data.gf);
      commit('setHcsPledgeByYear', data.data.hcs);
      commit('setTitheStartByYear', data.data.tithe);
      commit('setTitheContinueByYear', data.data.tithe_continue);
    } catch (e) {
      reportRecordError(
        { commit, dispatch },
        e,
        'Failed to retrieve building fund pledge by years',
      );
    }
  },
  async getTitheRecordByYear({ commit, dispatch, rootState }, year) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/tithes?year=' + year,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting tithe record by year');
      commit('setTitheRecordByYear', data);
    } catch (e) {
      reportRecordError({ commit, dispatch }, e, 'Failed to retrieve tithe record by year');
    }
  },
  async getBfRecordByYear({ commit, dispatch, rootState }, year) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/fulfillments?year=' + year,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting building fund record by year');
      Object.keys(data.data).forEach(key => {
        data.data[key] = data.data[key].sort((a, b) => {
          // 1. Access the date string from the object property
          const dateA = a.giving_date;
          const dateB = b.giving_date;

          // 2. Convert to Date objects and subtract for comparison
          return new Date(dateA) - new Date(dateB);
        });
      });
      commit('setBfRecordByYear', data.data.bf);
      commit('setGfRecordByYear', data.data.gf);
      commit('setHcsRecordByYear', data.data.hcs);
    } catch (e) {
      reportRecordError({ commit, dispatch }, e, 'Failed to retrieve building fund record by year');
    }
  },
  async getTithingBfState({ commit, dispatch, rootState }, stateParam) {
    try {
      // State 0: No Tithing History , No BF History
      if (
        (stateParam?.titheYear === undefined ||
          stateParam?.titheYear === null ||
          stateParam?.titheYear.length === 0) &&
        (stateParam?.bfYear === undefined ||
          stateParam?.bfYear === null ||
          stateParam?.bfYear.length === 0)
      )
        commit('setTithingState', 0);
      // State 1: No Tithing History , Have BF History
      else if (
        (stateParam?.titheYear === undefined ||
          stateParam?.titheYear === null ||
          stateParam?.titheYear.length === 0) &&
        (stateParam?.bfYear !== undefined ||
          stateParam?.bfYear !== null ||
          stateParam?.bfYear.length !== 0)
      )
        commit('setTithingState', 1);
      // State 2: Have Tithing History, No BF History
      else if (
        (stateParam?.titheYear !== undefined ||
          stateParam?.titheYear !== null ||
          stateParam?.titheYear.length !== 0) &&
        (stateParam?.bfYear === undefined ||
          stateParam?.bfYear === null ||
          stateParam?.bfYear.length === 0)
      )
        commit('setTithingState', 2);
      // State 3: Have Tithing History, Have BF History
      else if (
        (stateParam?.titheYear !== undefined ||
          stateParam?.titheYear !== null ||
          stateParam?.titheYear.length !== 0) &&
        (stateParam?.bfYear !== undefined ||
          stateParam?.titheYear !== null ||
          stateParam?.bfYear.length !== 0)
      )
        commit('setTithingState', 3);
    } catch (e) {
      dispatch('toast/error', 'Failed to retrieve tithing and building fund state', { root: true });
    }
  },
  async getLinkedBankAccounts({ commit, dispatch, rootState }, year) {
    // DEV-ONLY: /egiro-states seeds fixture rows so every account state can be
    // viewed on the real Manage screen with no bank and no giving API.
    if (isEgiroHarnessActive()) {
      commit('setLinkedAccount', harnessAccounts());
      return;
    }
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/linked-accounts',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting building fund record by year');
      commit('setLinkedAccount', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to retrieve linked bank accounts', { root: true });
    }
  },
  async getRecurringGivingList({ commit, dispatch, rootState }) {
    // DEV-ONLY: keep the harness offline — Manage fetches this alongside accounts.
    if (isEgiroHarnessActive()) {
      commit('setRecurringGivingList', []);
      return;
    }
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        process.env.VUE_APP_GIVING_API_V2_URL + '/e-giro/recurring?status=ACTIVE',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (data && data.data && data.data.length > 0) {
        // Transform the data to map memberLinkedAccount to egiro for backward compatibility
        const transformedData = data.data.map(item => ({
          ...item,
          egiro: item.memberLinkedAccount || null,
        }));
        commit('setRecurringGivingList', transformedData);
      } else {
        commit('setRecurringGivingList', []);
      }
    } catch (e) {
      // An unverified login gives, but does not see its earlier recurring gifts.
      if (isGivingLoginUnverified(e)) commit('setRecurringGivingList', []);
      reportRecordError({ commit, dispatch }, e, 'Failed to retrieve recurring giving list');
    }
  },
  async createEgiroTransaction({ commit, dispatch, rootState }, props) {
    const { apiEndpoint, payLoad } = props;
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const response = await axios.post(apiEndpoint, payLoad, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (e) {
      dispatch('toast/error', 'Failed to create e-giro transaction', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async checkFastTransactionStatus({ commit, dispatch, rootState }, transactionId) {
    // DEV-ONLY: the BF2026 harness's FAST gifts (/bf-pledge-states).
    if (isBfFastHarnessTransaction(transactionId)) {
      return fakeFastStatus(transactionId);
    }
    try {
      const accessToken = rootState.user.token;
      const response = await axios.get(
        `${process.env.VUE_APP_GIVING_API_V2_URL}/e-giro/fast/status/${transactionId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response;
    } catch (e) {
      dispatch('toast/error', 'Failed to check transaction status', { root: true });
      throw e;
    }
  },
  async updateEgiroTransaction({ commit, dispatch, rootState }, props) {
    const { apiEndpoint, payLoad } = props;
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const response = await axios.put(apiEndpoint, payLoad, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (e) {
      dispatch('toast/error', 'Failed to update e-giro transaction', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async deleteEgiroTransaction({ commit, dispatch, rootState }, apiEndpoint) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const response = await axios.delete(apiEndpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (e) {
      dispatch('toast/error', 'Failed to delete e-giro transaction', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  /**
   * May this login see the member's giving? `GET /giving/login/state` on the
   * hogc-one-api that giving-api asks about the login (the PIN's, see
   * givingPinApiUrl). Any failure, a 404 from an older API included, leaves the
   * state unknown (null) so MyGiving behaves as before; giving-api still refuses
   * the records of an unverified login, and the app catches that answer too.
   */
  async getLoginGivingState({ commit, rootState }) {
    try {
      const { data } = await axios.get(`${givingPinApiUrl()}${GIVING_LOGIN_STATE_PATH}`, {
        headers: { Authorization: `Bearer ${rootState.user.token}` },
      });
      const loginState = normalizeLoginState(data);
      commit('setLoginGivingState', loginState);
      return loginState;
    } catch (e) {
      console.warn('[MyGiving] login giving state unavailable:', e?.response?.status ?? e?.message);
      commit('setLoginGivingState', null);
      return null;
    }
  },
  /**
   * The one-time personal-id check. Resolves `{ ok: true, state, isPrimaryLogin }`,
   * or `{ ok: false, error }` with the mapped error (see mapVerifyPersonalIdError).
   * Never rejects, and never logs what was typed.
   */
  async verifyPersonalId({ commit, rootState }, { partialNric, dateOfBirth }) {
    try {
      const { data } = await axios.post(
        `${givingPinApiUrl()}${VERIFY_PERSONAL_ID_PATH}`,
        verifyPersonalIdPayload({ partialNric, dateOfBirth }),
        { headers: { Authorization: `Bearer ${rootState.user.token}` } },
      );
      const loginState = normalizeLoginState(data) ?? {
        state: GIVING_LOGIN_STATES.VERIFIED,
        isPrimaryLogin: true,
      };
      commit('setLoginGivingState', loginState);
      return { ok: loginState.state === GIVING_LOGIN_STATES.VERIFIED, ...loginState };
    } catch (e) {
      const error = mapVerifyPersonalIdError(e);
      // The status and code only: the request body is the member's NRIC and birthday.
      console.warn('[MyGiving] personal id check failed:', e?.response?.status, error.kind);
      return { ok: false, error };
    }
  },
  async getPinState({ commit, state, rootState, dispatch }) {
    // DEV-ONLY: open the giving gate so the harness lands on Manage directly.
    // The PIN screens have their own states and are not what this harness drives.
    if (isEgiroHarnessActive()) {
      const state = { state: 'pin_verified', attemptsLeft: 3, lockedUntil: null };
      commit('setPinState', { status: state.state, attemptsLeft: 3, lockedUntil: null });
      commit('setPinVerifiedThisSession', true);
      return state;
    }
    try {
      const accessToken = rootState.user.token;
      // Sending the header (even as `none`) tells hogc-one-api to answer with
      // session rules instead of the legacy 15-minute `lastVerifiedAt` window, so
      // leaving to the homepage really ends the session.
      const { data } = await axios.get(`${givingPinApiUrl()}/giving/pin/state`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          [PIN_SESSION_HEADER]: state.pinSessionId || NO_PIN_SESSION,
        },
      });
      commit('setPinState', {
        status: data.state,
        attemptsLeft: data.attemptsLeft ?? null,
        lockedUntil: data.lockedUntil ?? null,
        lastVerifiedAt: data.lastVerifiedAt ?? null,
      });

      if (data.state === 'pin_verified' || data.state === 'pin_matched') {
        commit('setPinVerifiedThisSession', true);
        if (data.pinSessionId) commit('setPinSessionId', data.pinSessionId);
      } else {
        commit('clearPinSession');
      }

      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to get PIN state', { root: true });
      throw e;
    }
  },
  async createPin({ commit, dispatch, rootState }, { pin }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.post(
        `${givingPinApiUrl()}/giving/pin/create`,
        { pin },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      commit('setPinState', {
        status: data.state,
        attemptsLeft: 3,
        lockedUntil: null,
      });

      dispatch('toast/success', 'PIN created successfully', { root: true });
      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to create PIN', { root: true });
      throw e;
    } finally {
      commit('setIsLoading', false);
    }
  },
  async verifyPin({ commit, dispatch, rootState }, { pin }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.post(
        `${givingPinApiUrl()}/giving/pin/verify`,
        { pin },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      commit('setPinState', {
        status: data.state,
        attemptsLeft: data.attemptsLeft ?? null,
        lockedUntil: data.lockedUntil ?? null,
      });

      if (data.state === 'pin_matched') {
        commit('setPinVerifiedThisSession', true);
        // Older hogc-one-api returns no id; giving-api then only logs (log mode).
        if (data.pinSessionId) commit('setPinSessionId', data.pinSessionId);
      }

      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to verify PIN', { root: true });
      throw e;
    } finally {
      commit('setIsLoading', false);
    }
  },
  /**
   * End the PIN session: locally at once, then on the server (best effort — the
   * server's idle TTL ends it anyway). Called when the member goes home.
   */
  async endPinSession({ commit, state, rootState }) {
    const sessionId = state.pinSessionId;
    commit('clearPinSession');
    if (!sessionId) return;
    try {
      await axios.post(
        `${givingPinApiUrl()}/giving/pin/session/end`,
        {},
        {
          headers: {
            Authorization: `Bearer ${rootState.user.token}`,
            [PIN_SESSION_HEADER]: sessionId,
          },
        },
      );
    } catch (e) {
      console.warn('[MyGiving] could not end the PIN session on the server:', e?.message);
    }
  },
  async resetPinInit({ commit, rootState, dispatch }, { email }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;

      const { data } = await axios.put(
        `${givingPinApiUrl()}/giving/pin/reset/init`,
        { memberEmail: email },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      commit('setResetEmail', {
        status: data.state,
        email: email,
        masked: email,
      });
      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to start PIN reset', { root: true });
      throw e;
    } finally {
      commit('setIsLoading', false);
    }
  },

  async resetPinVerifyOtp({ commit, rootState, dispatch }, { email, otp }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;

      const { data } = await axios.put(
        `${givingPinApiUrl()}/giving/pin/reset/verify-otp`,
        { email, otp },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      commit('setPinState', { status: data.state });
      return data;
    } catch (e) {
      dispatch('toast/error', 'Incorrect or expired OTP', { root: true });
      throw e;
    } finally {
      commit('setIsLoading', false);
    }
  },

  async setNewPinAfterReset({ commit, rootState, dispatch }, { newPin }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;

      const { data } = await axios.post(
        `${givingPinApiUrl()}/giving/pin/reset/set-new`,
        { pin: newPin },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      commit('setPinState', {
        status: data.state,
        attemptsLeft: 3,
        lockedUntil: null,
      });
      dispatch('toast/success', 'PIN updated', { root: true });
      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to set new PIN', { root: true });
      throw e;
    } finally {
      commit('setIsLoading', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
